import { isBefore, parseISO } from 'date-fns'
import { PartialPost } from '@opallabs/continuum/src/WeekView/momentTypes'

export type ContentApprovalStatus =
  | 'approved'
  | 'declined'
  | 'denied'
  | 'default'
  | 'draft'
  | 'for_approval'
  | 'needs_revision'
  | 'partial'
  | 'pending'
  | 'progress'
  | 'published'

export type ApprovalStatuses = Array<PartialPost['status'] | 'published'>

const statusMap = {
  approved: 'is-approved',
  declined: 'is-denied',
  denied: 'is-denied',
  default: 'is-draft',
  draft: 'is-draft',
  for_approval: 'is-pending',
  needs_revision: 'is-denied',
  partial: 'is-approved is-pending',
  pending: 'is-pending',
  progress: 'is-approved is-draft',
  published: 'is-published',
} as const

const isScheduledInPast = (contentAttrs: PartialPost) => {
  if (contentAttrs.post_state === 'pending' && contentAttrs.scheduled_at) {
    return isBefore(parseISO(contentAttrs.scheduled_at), new Date())
  }
  return false
}

/**
 * Parses the status of the content based on a variety of values within its attributes. What gets
 * returned here should be appended as a CSS class in order to color the "approval dot" correctly.
 */
export const postStatus = (contentAttrs?: PartialPost) => {
  const validApprovalPostStates = ['published']
  const validStatusMap = {
    approved: 'approved',
    draft: 'draft',
    needs_revision: 'denied',
    for_approval: 'draft',
  } as const

  if (!contentAttrs) {
    return 'draft'
  }

  // some of our approval styles require us to use the `post_state` here, rather than `status`
  if (
    contentAttrs.post_state &&
    validApprovalPostStates.includes(contentAttrs.post_state)
  ) {
    return contentAttrs.post_state
  }

  if (contentAttrs.status && contentAttrs.status in validStatusMap) {
    return validStatusMap[contentAttrs.status]
  }

  if (isScheduledInPast(contentAttrs)) {
    return 'pending'
  }
  if (contentAttrs.post_state) {
    return contentAttrs.post_state
  }
  return 'draft'
}

const validStatuses = ['dispatched', 'held', 'failed', 'published']
/**
 * Parses the string for the delivery status of the post. In some cases, this will be the same
 * as the approval status, but we also need to account for specific/unique delivery types here.
 */
export const getDeliveryStatus = (contentAttrs?: PartialPost) => {
  if (
    contentAttrs &&
    contentAttrs.post_state &&
    validStatuses.includes(contentAttrs.post_state)
  ) {
    return contentAttrs.post_state
  }
  return postStatus(contentAttrs)
}

/**
 * Parses and returns the full CSS class for setting styles on the "delivery status ring"
 */
export const postPublishingMode = (
  contentAttrs?: PartialPost,
  deliveryStatus = ''
) => {
  let publishingMode = 'manual'
  if (contentAttrs && contentAttrs.publishing_mode) {
    publishingMode = contentAttrs.publishing_mode
  }
  return `kuiper-ring publishingStatus-ring sprite-approval_status is-${deliveryStatus}_${publishingMode}`
}

export const generateStatusString = ({
  entity,
  workflow,
  approvalStatuses,
}: {
  entity?: PartialPost
  workflow?: { content_approval_status: ContentApprovalStatus }
  approvalStatuses: ApprovalStatuses
}) => {
  let approvalStatusString = ''

  if (entity && entity.is_published) {
    approvalStatusString = `${approvalStatusString} is-published`
    return approvalStatusString
  }

  // unless a user has opened the workflow popover
  // we have not yet fetched workflow. In this case
  // workflow will not yet exist.
  if (!workflow || !workflow.content_approval_status) {
    approvalStatusString = approvalStatuses.reduce((acc, status) => {
      // line added to style 'is-ghost content items,
      // which currently return null values for status
      // using draft because the 'is-ghost' value does not render correctly
      const statusString = statusMap[status] || 'is-draft'
      return acc.length ? `${acc} ${statusString}` : statusString
    }, '')
  } else {
    approvalStatusString = statusMap[workflow.content_approval_status]
  }

  return approvalStatusString
}

export const ariaLabels = ({
  entity,
  workflow,
  approvalStatuses,
}: {
  entity?: PartialPost
  workflow?: { content_approval_status: ContentApprovalStatus }
  approvalStatuses: ApprovalStatuses
}) => {
  let approvalStatus = generateStatusString({
    entity,
    workflow,
    approvalStatuses,
  }).replace(/is-/g, '')
  let publishingStatus = getDeliveryStatus(entity)

  if (publishingStatus === approvalStatus) {
    publishingStatus = 'Not been published yet'
  }

  const approvalStatusArray = approvalStatus.split(/\s+/g)
  if (approvalStatusArray.length > 1) {
    approvalStatus = `Content statuses: ${approvalStatuses.join(', ')}`
  } else {
    approvalStatus = `Content is ${approvalStatus}`
  }

  return {
    approvalStatus,
    publishingStatus,
  }
}
